import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getPayrollByUserId, updatePayroll } from "../api/api";
import toast from "react-hot-toast";
import "./Payroll.css";
import FormGuidelines from "../components/FormGuidelines";

function isWeekend(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const day = d.getDay(); // 5 = Friday, 6 = Saturday
  return day === 5 || day === 6;
}

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const currentMonth = () => MONTHS[new Date().getMonth()];

function getInitials(name = "") {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

const DEPT_COLORS = {
  Software:  { color: "#60a5fa", bg: "#0d1f3c" },
  Marketing: { color: "#f472b6", bg: "#2d0a1e" },
  CEO:       { color: "#facc15", bg: "#1e1500" },
  HR:        { color: "#a78bfa", bg: "#1a1040" },
  Other:     { color: "#94a3b8", bg: "#1a2540" },
};

export default function EmployeePayrollProfile() {
  const navigate  = useNavigate();
  const { empId } = useParams();
  const location  = useLocation();

  const [payroll, setPayroll] = useState(location.state?.payroll || null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(location.state?.selectedMonth || currentMonth());
  const [activeTab, setActiveTab] = useState("overview");

  const [showAbsModal, setShowAbsModal] = useState(false);
  const [showBonModal, setShowBonModal] = useState(false);
  const [showDedModal, setShowDedModal] = useState(false);
  const [showSalModal, setShowSalModal] = useState(false);

  const [newAbs, setNewAbs] = useState({ date: "", reason: "", deductionAmount: "" });
  const [newBon, setNewBon] = useState({ date: "", reason: "", amount: "" });
  const [newDed, setNewDed] = useState({ date: "", reason: "", amount: "" });
  const [newSal, setNewSal] = useState("");

  const fetchPayroll = async () => {
    try {
      setLoading(true);
      console.log("Fetching payroll for empId:", empId);
      const data = await getPayrollByUserId(empId);
      const payrollData = data?.data || data;
      console.log("Fetched payroll data for empId:", empId, payrollData);
      // If payroll is an array, filter by selected month
      if (Array.isArray(payrollData)) {
        const monthlyPayroll = payrollData.find(p => p.month === selectedMonth);
        setPayroll(monthlyPayroll || payrollData[0] || null);
      } else if (payrollData) {
        // Single payroll record - use it regardless of month
        setPayroll(payrollData);
      } else {
        setPayroll(null);
      }
    } catch (err) {
      // Handle case where payroll is not found gracefully
      console.error("Failed to load payroll for empId:", empId, err);
      setPayroll(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!empId) {
      console.error("No empId provided in URL");
      setPayroll(null);
      setLoading(false);
      return;
    }
    fetchPayroll();
  }, [empId, selectedMonth]);

  if (loading) return <div className="payroll-loading">Loading...</div>;

  if (!payroll) return (
    <div style={{ color: "#fff", padding: 40, textAlign: "center" }}>
      Payroll not found.
      <button onClick={() => navigate("/finance")} style={{ display: "block", margin: "20px auto", padding: "10px 24px", background: "#facc15", color: "#000", border: "none", borderRadius: 8, cursor: "pointer" }}>
        ← Back to Finance
      </button>
    </div>
  );

  const emp = payroll.userId || payroll;
  const updatePayrollData = async (patch) => {
    try {
      const payload = { ...patch };
      // Only add month if it's not already set
      if (!payroll.month) {
        payload.month = selectedMonth;
      }
      await updatePayroll(payroll._id || payroll.id, payload);
      fetchPayroll();
      toast.success("Updated successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to update");
    }
  };

  // Filter by month — absences also exclude weekends
  const absences   = (payroll.absences   || []).filter(a => (!a.month || a.month === selectedMonth) && !isWeekend(a.date));
  const bonuses    = (payroll.bonuses    || []).filter(b => !b.month || b.month === selectedMonth);
  const deductions = (payroll.deductions || []).filter(d => !d.month || d.month === selectedMonth);

  const absAmt = absences.reduce((s, a)   => s + (a.deductionAmount || 0), 0);
  const bonAmt = bonuses.reduce((s, b)    => s + (b.amount || 0), 0);
  const dedAmt = deductions.reduce((s, d) => s + (d.amount || 0), 0);
  const net    = payroll.basicSalary - absAmt - dedAmt + bonAmt;

  const dept = DEPT_COLORS[emp.department] || { color: "#94a3b8", bg: "#1a2540" };

  // ── Handlers ──────────────────────────────────────────────
  const handleAddAbsence = async () => {
    if (!newAbs.date || !newAbs.deductionAmount) return;
    if (isWeekend(newAbs.date)) {
      toast.error("الجمعة والسبت إجازات رسمية ولا تُحسب كغياب");
      return;
    }
    const currentAbsences = typeof payroll.absences === 'number' ? payroll.absences : 0;
    const newAbsenceAmount = parseFloat(newAbs.deductionAmount);
    await updatePayrollData({ absences: currentAbsences + newAbsenceAmount });
    setNewAbs({ date: "", reason: "", deductionAmount: "" });
    setShowAbsModal(false);
  };

  const handleAddBonus = async () => {
    if (!newBon.date || !newBon.amount) return;
    const currentBonus = typeof payroll.bonus === 'number' ? payroll.bonus : 0;
    const newBonusAmount = parseFloat(newBon.amount);
    await updatePayrollData({ bonus: currentBonus + newBonusAmount });
    setNewBon({ date: "", reason: "", amount: "" });
    setShowBonModal(false);
  };

  const handleAddDeduction = async () => {
    if (!newDed.date || !newDed.amount) return;
    const currentDeduction = typeof payroll.deduction === 'number' ? payroll.deduction : 0;
    const newDeductionAmount = parseFloat(newDed.amount);
    await updatePayrollData({ deduction: currentDeduction + newDeductionAmount });
    setNewDed({ date: "", reason: "", amount: "" });
    setShowDedModal(false);
  };

  const handleUpdateSalary = async () => {
    if (!newSal) return;
    await updatePayrollData({ basicSalary: parseFloat(newSal) });
    setNewSal("");
    setShowSalModal(false);
  };

  const handleDelete = async (type, id) => {
    // Since absences, bonus, and deduction are now numbers, we can't delete individual entries
    // This function is disabled for now
    toast.error("Cannot delete individual entries. Use edit to adjust values.");
  };

  return (
    <div className="payroll-page">

      {/* ── Back ── */}
      <button className="back-btn-pay" onClick={() => navigate("/finance")}>
        ← Back to Payroll
      </button>

      {/* ── Profile Header ── */}
      <div className="emp-profile-card">
        <div className="emp-profile-left">
          <div className="emp-profile-avatar">{getInitials(emp.userName || emp.name)}</div>
          <div>
            <div className="emp-profile-name">{emp.userName || emp.name}</div>
            <div className="emp-profile-role">{emp.position || emp.role}</div>
            <span className="dept-badge" style={{ color: dept.color, background: dept.bg, marginTop: 8, display: "inline-block" }}>
              {emp.department}
            </span>
          </div>
        </div>
        <div className="emp-profile-right">
          <select className="month-select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <button className="edit-sal-btn" onClick={() => { setNewSal(payroll.basicSalary); setShowSalModal(true); }}>
            ✏️ Edit Base Salary
          </button>
        </div>
      </div>

      {/* ── Salary Summary ── */}
      <div className="salary-summary">
        {[
          { label: "Base Salary", value: payroll.basicSalary.toLocaleString() + " EGP", color: "#facc15" },
          { label: "Absences",    value: "-" + absAmt.toLocaleString()   + " EGP", color: "#f87171" },
          { label: "Deductions",  value: "-" + dedAmt.toLocaleString()   + " EGP", color: "#fb923c" },
          { label: "Bonuses",     value: "+" + bonAmt.toLocaleString()   + " EGP", color: "#34d399" },
          { label: "Net Salary",  value: net.toLocaleString()            + " EGP", color: "#fff", big: true },
        ].map(s => (
          <div className={`sal-card ${s.big ? "sal-card-net" : ""}`} key={s.label}>
            <div className="sal-label">{s.label}</div>
            <div className="sal-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="profile-tabs">
        {["overview", "absences", "bonuses", "deductions"].map(t => (
          <button key={t} className={`tab-btn ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === "absences"   && absences.length   > 0 && <span className="tab-count red">{absences.length}</span>}
            {t === "bonuses"    && bonuses.length     > 0 && <span className="tab-count green">{bonuses.length}</span>}
            {t === "deductions" && deductions.length  > 0 && <span className="tab-count orange">{deductions.length}</span>}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <div className="tab-content">

        {/* Overview */}
        {activeTab === "overview" && (
          <div className="overview-grid">
            <div className="overview-section">
              <div className="overview-sec-header">
                <h4>Absences <span className="count-chip red">{absences.length}</span></h4>
                <button className="add-record-btn" onClick={() => setShowAbsModal(true)}>+ Add</button>
              </div>
              {absences.length === 0
                ? <div className="empty-msg">No absences this month 🎉</div>
                : absences.slice(0, 3).map(a => (
                  <div className="record-row" key={a.id}>
                    <div>
                      <div className="record-date">{a.date}</div>
                      <div className="record-reason">{a.reason || "—"}</div>
                    </div>
                    <div className="record-amount red">-{(a.deductionAmount || 0).toLocaleString()} EGP</div>
                  </div>
                ))
              }
            </div>

            <div className="overview-section">
              <div className="overview-sec-header">
                <h4>Bonuses <span className="count-chip green">{bonuses.length}</span></h4>
                <button className="add-record-btn" onClick={() => setShowBonModal(true)}>+ Add</button>
              </div>
              {bonuses.length === 0
                ? <div className="empty-msg">No bonuses this month</div>
                : bonuses.slice(0, 3).map(b => (
                  <div className="record-row" key={b.id}>
                    <div>
                      <div className="record-date">{b.date}</div>
                      <div className="record-reason">{b.reason || "—"}</div>
                    </div>
                    <div className="record-amount green">+{(b.amount || 0).toLocaleString()} EGP</div>
                  </div>
                ))
              }
            </div>

            <div className="overview-section">
              <div className="overview-sec-header">
                <h4>Deductions <span className="count-chip orange">{deductions.length}</span></h4>
                <button className="add-record-btn" onClick={() => setShowDedModal(true)}>+ Add</button>
              </div>
              {deductions.length === 0
                ? <div className="empty-msg">No deductions this month</div>
                : deductions.slice(0, 3).map(d => (
                  <div className="record-row" key={d.id}>
                    <div>
                      <div className="record-date">{d.date}</div>
                      <div className="record-reason">{d.reason || "—"}</div>
                    </div>
                    <div className="record-amount red">-{(d.amount || 0).toLocaleString()} EGP</div>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* Absences Tab */}
        {activeTab === "absences" && (
          <div className="records-section">
            <div className="records-header">
              <h4>Absences — {selectedMonth}</h4>
              <button className="add-record-btn primary" onClick={() => setShowAbsModal(true)}>+ Add Absence</button>
            </div>
            {absences.length === 0
              ? <div className="empty-msg">No absences recorded for {selectedMonth}</div>
              : (
                <div className="records-list">
                  {absences.map(a => (
                    <div className="record-card" key={a.id}>
                      <div className="record-card-left">
                        <div className="record-icon abs-icon">📅</div>
                        <div>
                          <div className="record-card-date">{a.date}</div>
                          <div className="record-card-reason">{a.reason || "No reason provided"}</div>
                        </div>
                      </div>
                      <div className="record-card-right">
                        <div className="record-card-amount red">-{(a.deductionAmount || 0).toLocaleString()} EGP</div>
                        <button className="del-record-btn" onClick={() => handleDelete("absences", a.id)}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
            <div className="records-total">
              Total deducted: <span className="red">{absAmt.toLocaleString()} EGP</span>
            </div>
          </div>
        )}

        {/* Bonuses Tab */}
        {activeTab === "bonuses" && (
          <div className="records-section">
            <div className="records-header">
              <h4>Bonuses — {selectedMonth}</h4>
              <button className="add-record-btn primary" onClick={() => setShowBonModal(true)}>+ Add Bonus</button>
            </div>
            {bonuses.length === 0
              ? <div className="empty-msg">No bonuses recorded for {selectedMonth}</div>
              : (
                <div className="records-list">
                  {bonuses.map(b => (
                    <div className="record-card" key={b.id}>
                      <div className="record-card-left">
                        <div className="record-icon bon-icon">⭐</div>
                        <div>
                          <div className="record-card-date">{b.date}</div>
                          <div className="record-card-reason">{b.reason || "No reason provided"}</div>
                        </div>
                      </div>
                      <div className="record-card-right">
                        <div className="record-card-amount green">+{(b.amount || 0).toLocaleString()} EGP</div>
                        <button className="del-record-btn" onClick={() => handleDelete("bonuses", b.id)}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
            <div className="records-total">
              Total bonuses: <span className="green">{bonAmt.toLocaleString()} EGP</span>
            </div>
          </div>
        )}

        {/* Deductions Tab */}
        {activeTab === "deductions" && (
          <div className="records-section">
            <div className="records-header">
              <h4>Deductions — {selectedMonth}</h4>
              <button className="add-record-btn primary" onClick={() => setShowDedModal(true)}>+ Add Deduction</button>
            </div>
            {deductions.length === 0
              ? <div className="empty-msg">No deductions recorded for {selectedMonth}</div>
              : (
                <div className="records-list">
                  {deductions.map(d => (
                    <div className="record-card" key={d.id}>
                      <div className="record-card-left">
                        <div className="record-icon ded-icon">📉</div>
                        <div>
                          <div className="record-card-date">{d.date}</div>
                          <div className="record-card-reason">{d.reason || "No reason provided"}</div>
                        </div>
                      </div>
                      <div className="record-card-right">
                        <div className="record-card-amount red">-{(d.amount || 0).toLocaleString()} EGP</div>
                        <button className="del-record-btn" onClick={() => handleDelete("deductions", d.id)}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
            <div className="records-total">
              Total deducted: <span className="red">{dedAmt.toLocaleString()} EGP</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}

      {/* Absence Modal */}
      {showAbsModal && (
        <Modal title="Record Absence" onClose={() => setShowAbsModal(false)} onSave={handleAddAbsence} saveLabel="Record">
          <ModalField label="Date (الجمعة والسبت إجازات رسمية)" type="date" value={newAbs.date} onChange={v => setNewAbs(p => ({ ...p, date: v }))} />
          <ModalField label="Reason" value={newAbs.reason} placeholder="e.g. Sick leave" onChange={v => setNewAbs(p => ({ ...p, reason: v }))} />
          <ModalField label="Deduction Amount (EGP)" type="number" value={newAbs.deductionAmount} placeholder="500" onChange={v => setNewAbs(p => ({ ...p, deductionAmount: v }))} />
          {newAbs.date && isWeekend(newAbs.date) && (
            <div style={{ background: "#2d0a0a", border: "1px solid #f8717130", borderRadius: 8, padding: "10px 14px", color: "#f87171", fontSize: 13 }}>
              ⚠️ هذا اليوم إجازة رسمية (جمعة/سبت) ولن يُحسب كغياب
            </div>
          )}
        </Modal>
      )}

      {/* Bonus Modal */}
      {showBonModal && (
        <Modal title="Add Bonus" onClose={() => setShowBonModal(false)} onSave={handleAddBonus} saveLabel="Add Bonus" saveColor="#34d399">
          <ModalField label="Date" type="date" value={newBon.date} onChange={v => setNewBon(p => ({ ...p, date: v }))} />
          <ModalField label="Reason" value={newBon.reason} placeholder="e.g. Performance bonus" onChange={v => setNewBon(p => ({ ...p, reason: v }))} />
          <ModalField label="Amount (EGP)" type="number" value={newBon.amount} placeholder="1000" onChange={v => setNewBon(p => ({ ...p, amount: v }))} />
        </Modal>
      )}

      {/* Deduction Modal */}
      {showDedModal && (
        <Modal title="Add Deduction" onClose={() => setShowDedModal(false)} onSave={handleAddDeduction} saveLabel="Add Deduction" saveColor="#fb923c">
          <ModalField label="Date" type="date" value={newDed.date} onChange={v => setNewDed(p => ({ ...p, date: v }))} />
          <ModalField label="Reason" value={newDed.reason} placeholder="e.g. Equipment damage" onChange={v => setNewDed(p => ({ ...p, reason: v }))} />
          <ModalField label="Amount (EGP)" type="number" value={newDed.amount} placeholder="200" onChange={v => setNewDed(p => ({ ...p, amount: v }))} />
        </Modal>
      )}

      {/* Edit Salary Modal */}
      {showSalModal && (
        <Modal title="Edit Base Salary" onClose={() => setShowSalModal(false)} onSave={handleUpdateSalary} saveLabel="Update">
          <ModalField label="New Base Salary (EGP)" type="number" value={newSal} placeholder={emp.baseSalary} onChange={v => setNewSal(v)} />
        </Modal>
      )}
    </div>
  );
}

// ── Reusable Modal ─────────────────────────────────────────────
function Modal({ title, onClose, onSave, saveLabel = "Save", saveColor = "#facc15", children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <FormGuidelines />
          {children}
        </div>
        <div className="modal-footer">
          <button className="modal-cancel" onClick={onClose}>Cancel</button>
          <button
            className="modal-save"
            style={{ background: saveColor, color: saveColor === "#facc15" ? "#0a0f1a" : "#fff" }}
            onClick={onSave}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div className="modal-field">
      <label>{label}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    </div>
  );
}